import type { GameState, LaneId, PlayerId } from "../types";
import { isActiveLandForPlayer } from "./formation";

function sum(values: readonly number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

export function getArmyAttackContribution(
  state: Readonly<GameState>,
  playerId: PlayerId,
  laneId: LaneId,
): number {
  const lane = state.lanes[laneId];
  const side = lane.players[playerId];

  if (!isActiveLandForPlayer(state, playerId, laneId) || side.commander === null) {
    return 0;
  }

  return sum(side.armies.map((army) => army.attack));
}

export function getTotalAttack(
  state: Readonly<GameState>,
  playerId: PlayerId,
  laneId: LaneId,
): number {
  const lane = state.lanes[laneId];
  const side = lane.players[playerId];

  if (!isActiveLandForPlayer(state, playerId, laneId)) {
    return 0;
  }

  const commander = side.commander;
  if (commander === null) {
    return 0;
  }

  return (
    commander.attack +
    getArmyAttackContribution(state, playerId, laneId) +
    (side.land?.attackBonus ?? 0) +
    sum(side.combatModifiers.map((modifier) => modifier.attack))
  );
}

export function getTotalGuard(
  state: Readonly<GameState>,
  playerId: PlayerId,
  laneId: LaneId,
): number {
  const lane = state.lanes[laneId];
  const side = lane.players[playerId];

  if (!isActiveLandForPlayer(state, playerId, laneId)) {
    return 0;
  }

  return (
    (side.commander?.guard ?? 0) +
    sum(side.armies.map((army) => army.guard)) +
    (side.land?.guardBonus ?? 0) +
    sum(side.combatModifiers.map((modifier) => modifier.guard))
  );
}

export function calculateLandDamage(totalAttack: number, totalGuard: number): number {
  return Math.max(0, totalAttack - totalGuard);
}
